import React, { useState, useEffect, useCallback } from 'react';
import { api, tomorrow, fmtDate } from '../api';

const HOTEL_COLORS = [
  ['#667eea','#764ba2'], ['#f093fb','#f5576c'], ['#4facfe','#00f2fe'],
  ['#43e97b','#38f9d7'], ['#fa709a','#fee140'], ['#a18cd1','#fbc2eb'],
  ['#fda085','#f6d365'], ['#89f7fe','#66a6ff'],
];

function hotelColor(name) {
  let h = 0;
  for (let i = 0; i < name.length; i++) h = (h * 31 + name.charCodeAt(i)) & 0xffff;
  const [a, b] = HOTEL_COLORS[h % HOTEL_COLORS.length];
  return `linear-gradient(135deg, ${a}, ${b})`;
}

function hotelInitials(name) {
  return name.split(' ').filter(w => w.length > 2).map(w => w[0]).join('').slice(0, 2).toUpperCase() || name.slice(0,2).toUpperCase();
}

function getStatus(row) {
  if (!row.session_id) return { cls: 'status-new',     label: 'Новый' };
  if (row.status === 'locked') return { cls: 'status-ready',   label: 'Готов к отправке' };
  if (row.total_records > 0)   return { cls: 'status-working', label: 'В работе' };
  return { cls: 'status-new', label: 'Новый' };
}

function HotelMenu({ r, user, onUnlock, onDelete }) {
  const [open, setOpen] = React.useState(false);
  const isLocked = r.status === 'locked';
  const isSA = user && user.role === 'superadmin';
  const items = [];
  if (isSA && isLocked && r.session_id) items.push({ label: '🔓 Разблокировать', danger: false, fn: () => { onUnlock(r.session_id); setOpen(false); } });
  if (isSA && r.session_id) items.push({ label: '🗑 Удалить сессию', danger: true, fn: () => { onDelete(r.session_id); setOpen(false); } });
  if (!items.length) return <button className="btn-icon" style={{fontSize:13,opacity:0.25,cursor:'default'}}>⋯</button>;
  return (
    <div style={{position:'relative'}}>
      <button className="btn-icon" style={{fontSize:13}} onClick={() => setOpen(o => !o)}>⋯</button>
      {open && <>
        <div style={{position:'fixed',inset:0,zIndex:99}} onClick={() => setOpen(false)} />
        <div style={{position:'absolute',right:0,top:'110%',background:'#fff',border:'1px solid #e5e7eb',borderRadius:8,boxShadow:'0 4px 16px rgba(0,0,0,0.13)',zIndex:100,minWidth:180,overflow:'hidden'}}>
          {items.map((it, i) => (
            <button key={i} onClick={it.fn} style={{display:'block',width:'100%',padding:'10px 15px',background:'none',border:'none',borderBottom:i<items.length-1?'1px solid #f3f4f6':'none',textAlign:'left',cursor:'pointer',fontSize:13,color:it.danger?'#e74c3c':'#333'}}>
              {it.label}
            </button>
          ))}
        </div>
      </>}
    </div>
  );
}

function HotelRow({ r, onOpen, onSync, syncing, canSync, user, onUnlock, onDelete, showRooms }) {
  const st = getStatus(r);
  const qty = (t) => {
    const v = r[`${t}_qty`] || 0;
    return <span className={`hotel-qty${v === 0 ? ' zero' : ''}`}>{v}</span>;
  };
  const rooms = showRooms && r.rooms_detail
    ? [...new Map(r.rooms_detail.map(x => [x.room, x])).values()].sort((a,b) => (a.room||'').localeCompare(b.room||'', undefined, {numeric:true}))
    : [];
  return (
    <div className="hotel-row">
      <div className="hotel-info">
        <div className="hotel-avatar" style={{ background: hotelColor(r.hotel_name) }}>
          {hotelInitials(r.hotel_name)}
        </div>
        <div>
          <div className="hotel-name">{r.hotel_name}</div>
          {rooms.length > 0 && (
            <div style={{fontSize:11,color:'#999',marginTop:3,lineHeight:1.6}}>
              {rooms.map(x => (
                <span key={x.room} style={{display:'inline-block',marginRight:8,whiteSpace:'nowrap'}}>
                  🚪<b style={{color:'#555'}}>{x.room || '—'}</b>{x.guest ? ` ${x.guest.split(' ').slice(0,2).join(' ')}` : ''}
                </span>
              ))}
            </div>
          )}
        </div>
      </div>
      {qty('breakfast')} {qty('lunch')} {qty('dinner')}
      <div><span className={`status-badge ${st.cls}`}>{st.label}</span></div>
      <div style={{ display:'flex', gap:5, alignItems:'center' }}>
        {canSync && r.has_tl_access && (
          <button className="btn btn-tl btn-sm" onClick={() => onSync(r.hotel_id)} disabled={syncing}>
            {syncing ? '⏳' : '⚡'}
          </button>
        )}
        {r.session_id
          ? <button className="btn btn-ghost btn-sm" onClick={() => onOpen(r.session_id)}>Открыть</button>
          : <button className="btn btn-green btn-sm" onClick={() => onOpen(null, r.hotel_id)}>+ Создать</button>
        }
        <HotelMenu r={r} user={user} onUnlock={onUnlock} onDelete={onDelete} />
      </div>
    </div>
  );
}

function Panel({ type, rows, onOpen, onSync, syncing, canSync, user, onUnlock, onDelete, onSendIiko, day }) {
  const [expanded, setExpanded] = useState(false);
  const cfg = type === 'delivery'
    ? { icon: '🚚', title: 'На доставку', desc: 'Заказы, которые нужно доставить в номера', badgeCls: 'delivery' }
    : { icon: '🍴', title: 'В ресторане', desc: 'Заказы на обслуживание в ресторане отеля', badgeCls: 'restaurant' };

  const visible = expanded ? rows : rows.slice(0, 8);
  const hidden = rows.length - 8;

  return (
    <div className="panel">
      <div className="panel-header">
        <div className={`panel-icon ${type}`}>{cfg.icon}</div>
        <div className="panel-title">
          <h3>{cfg.title}</h3>
          <p>{cfg.desc}</p>
        </div>
        <span className={`panel-badge ${cfg.badgeCls}`}>{rows.length} отелей</span>
        <span className="panel-arrow" onClick={() => {}}>›</span>
      </div>
      {type === 'delivery' && (
        <div style={{padding:'8px 16px 4px',borderBottom:'1px solid #f0f0f0'}}>
          <button onClick={() => onSendIiko && onSendIiko(day)}
            style={{background:'#e74c3c',color:'#fff',border:'none',borderRadius:8,padding:'8px 18px',fontSize:13,cursor:'pointer',fontWeight:600,width:'100%'}}>
            🍽 Отправить завтраки доставки в iiko
          </button>
        </div>
      )}
      <div className="hotel-table-header">
        <span>Отель</span>
        <span style={{textAlign:'center',fontSize:'12px',whiteSpace:'nowrap'}}>🥐 Завтрак</span>
        <span style={{textAlign:'center',fontSize:'12px',whiteSpace:'nowrap'}}>🍲 Обед</span>
        <span style={{textAlign:'center',fontSize:'12px',whiteSpace:'nowrap'}}>🌙 Ужин</span>
        <span>Статус</span>
        <span>Действия</span>
      </div>
      {visible.map(r => (
        <HotelRow key={r.hotel_id} r={r} onOpen={onOpen} onSync={onSync}
          syncing={!!syncing[r.hotel_id]} canSync={canSync} user={user} onUnlock={onUnlock} onDelete={onDelete}
          showRooms={type === 'delivery'} />
      ))}
      {!expanded && hidden > 0 && (
        <div className="panel-show-more">
          <button className="show-more-btn" onClick={() => setExpanded(true)}>
            Показать ещё {hidden} отеля ▾
          </button>
        </div>
      )}
      {expanded && hidden > 0 && (
        <div className="panel-show-more">
          <button className="show-more-btn" onClick={() => setExpanded(false)}>
            Свернуть ▴
          </button>
        </div>
      )}
    </div>
  );
}

export default function DashboardPage({ user, onOpenSession }) {
  const [day, setDay]         = useState(tomorrow());
  const [rows, setRows]       = useState([]);
  const [hotels, setHotels]   = useState([]);
  const [loading, setLoading] = useState(false);
  const [syncing, setSyncing] = useState({});
  const [confirming, setConfirming] = useState(false);
  const [lastUpdate, setLastUpdate] = useState(null);

  const load = useCallback(async (silent = false) => {
    if (!silent) setLoading(true);
    try {
      const [r, h] = await Promise.all([api.day(day), api.hotels()]);
      setRows(r); setHotels(h); setLastUpdate(new Date());
    } catch(e) { console.error(e); }
    finally { if (!silent) setLoading(false); }
  }, [day]);

  useEffect(() => { load(); }, [load]);

  const canManage = user.role === 'superadmin' || user.role === 'manager';

  const unlockSession = async (sid) => {
    if (!window.confirm('Разблокировать?')) return;
    try { await api.unlock(sid); await load(true); } catch(e) { alert(e.message); }
  };
  const deleteSession = async (sid) => {
    if (!window.confirm('Удалить сессию безвозвратно?')) return;
    try { await api.deleteSession(sid); await load(true); } catch(e) { alert(e.message); }
  };

  const syncHotel = async (hotelId) => {
    setSyncing(s => ({...s, [hotelId]: true}));
    try { await api.sync(hotelId, day); await load(true); }
    catch(e) { alert(e.message); }
    finally { setSyncing(s => ({...s, [hotelId]: false})); }
  };

  const syncAll = async () => {
    setSyncing({all: true});
    try { await api.syncAll(day); await load(true); }
    catch(e) { alert(e.message); }
    finally { setSyncing({}); }
  };

  const confirmAll = async () => {
    const open = rows.filter(r => r.session_id && r.status !== 'locked').length;
    if (!open) { alert('Нет открытых сессий'); return; }
    if (!window.confirm(`Закрыть все ${open} сессий за ${day}?`)) return;
    setConfirming(true);
    try { await api.confirmAll(day); await load(true); }
    catch(e) { alert(e.message); }
    finally { setConfirming(false); }
  };

  const openSession = async (sessionId, hotelId) => {
    if (sessionId) { onOpenSession(sessionId); return; }
    try {
      const hotel = rows.find(r => r.hotel_id === hotelId);
      if (hotel && hotel.has_tl_access) {
        const res = await api.sync(hotelId, day);
        await load(true);
        onOpenSession(res.session_id);
      } else {
        const res = await api.createSession(hotelId, day);
        onOpenSession(res.session_id);
      }
    } catch(e) { alert(e.message); }
  };

  // Split by type
  const sendToIiko = async () => {
    if (!window.confirm(`Отправить завтраки доставки за ${day} в iiko?`)) return;
    try {
      const res = await api.sendIiko(day);
      alert(`✅ Отправлено в iiko!\nБлюдо: ${res.dish}\nИтого: ${res.total_qty} завтраков`);
    } catch(e) { alert('❌ Ошибка: ' + e.message); }
  };

  const delivery   = rows.filter(r => r.meal_service_type === 'delivery' || r.meal_service_type === 'mixed');
  const restaurant = rows.filter(r => r.meal_service_type === 'restaurant');

  const totalBreakfast = rows.reduce((s,r) => s+(r.breakfast_qty||0), 0);
  const totalLunch     = rows.reduce((s,r) => s+(r.lunch_qty||0), 0);
  const totalDinner    = rows.reduce((s,r) => s+(r.dinner_qty||0), 0);
  const deliveryQty    = delivery.reduce((s,r) => s+(r.breakfast_qty||0)+(r.lunch_qty||0)+(r.dinner_qty||0), 0);
  const restaurantQty  = restaurant.reduce((s,r) => s+(r.breakfast_qty||0)+(r.lunch_qty||0)+(r.dinner_qty||0), 0);
  const locked         = rows.filter(r=>r.status==='locked').length;
  const openSessions   = rows.filter(r=>r.session_id && r.status!=='locked').length;
  const isSyncing      = Object.values(syncing).some(Boolean);

  const fmtLastUpdate = lastUpdate
    ? lastUpdate.toLocaleString('ru-RU', {day:'2-digit',month:'2-digit',year:'numeric',hour:'2-digit',minute:'2-digit'})
    : '—';

  return (
    <div className="page">
      {/* Header */}
      <div className="page-header">
        <div>
          <div className="page-title">Дашборд</div>
        </div>
        <div className="header-actions">
          {/* Date picker */}
          <input type="date" value={day} onChange={e => setDay(e.target.value)}
            style={{ padding:'8px 12px', border:'1.5px solid #e5e7eb', borderRadius:10, fontSize:14, fontWeight:600, background:'#fff', cursor:'pointer' }} />
          <div className="last-update">
            <strong>Обновить</strong>
            <span>Последнее: {fmtLastUpdate}</span>
          </div>
          <button className="btn-icon" onClick={() => load()} title="Обновить" style={{fontSize:18}}>↻</button>
          {canManage && (
            <button className="btn btn-primary" onClick={syncAll} disabled={isSyncing}>
              {syncing.all ? '⏳ Синхронизация...' : '⚡ Синк всех из TL'}
            </button>
          )}
          {canManage && openSessions > 0 && (
            <button className="btn btn-orange" onClick={confirmAll} disabled={confirming}>
              {confirming ? '...' : `✅ Закрыть все (${openSessions})`}
            </button>
          )}
        </div>
      </div>

      {/* Stats */}
      <div className="stats-row">
        <div className="stat-card">
          <div className="stat-icon breakfast">🥐</div>
          <div className="stat-body">
            <div className="stat-label">Завтраки</div>
            <div className="stat-value">{totalBreakfast}</div>
            <div className="stat-breakdown"><span className="bd-delivery">🚚 {deliveryQty}</span><span className="bd-sep"> · </span><span className="bd-restaurant">🍴 {restaurantQty}</span></div>
          </div>
          <span className="stat-arrow">›</span>
        </div>
        <div className="stat-card">
          <div className="stat-icon lunch">🍲</div>
          <div className="stat-body">
            <div className="stat-label">Обеды</div>
            <div className="stat-value">{totalLunch}</div>
          </div>
          <span className="stat-arrow">›</span>
        </div>
        <div className="stat-card">
          <div className="stat-icon dinner">🌙</div>
          <div className="stat-body">
            <div className="stat-label">Ужины</div>
            <div className="stat-value">{totalDinner}</div>
          </div>
          <span className="stat-arrow">›</span>
        </div>
        <div className="stat-card">
          <div className="stat-icon closed">📋</div>
          <div className="stat-body">
            <div className="stat-label">Закрыто</div>
            <div className="stat-value">{locked} / {rows.length}</div>
            <div className="stat-progress">
              <div className="progress-bar">
                <div className="progress-fill" style={{ width: rows.length ? `${locked/rows.length*100}%` : '0%' }} />
              </div>
              <div className="progress-label">{rows.length ? Math.round(locked/rows.length*100) : 0}%</div>
            </div>
          </div>
        </div>
      </div>

      {/* Panels */}
      {loading ? <div className="empty">Загрузка...</div> : (
        <div className="panels-row">
          <Panel type="delivery" rows={delivery} onOpen={openSession}
            onSync={syncHotel} syncing={syncing} canSync={canManage} user={user} onUnlock={unlockSession} onDelete={deleteSession}
            onSendIiko={sendToIiko} day={day} />
          <Panel type="restaurant" rows={restaurant} onOpen={openSession}
            onSync={syncHotel} syncing={syncing} canSync={canManage} user={user} onUnlock={unlockSession} onDelete={deleteSession} />
        </div>
      )}
    </div>
  );
}
